import React, { useState, useEffect, useCallback } from 'react';
import { useActiveCareerFocus } from '../../hooks/useCareerFocus';
import { useGoals } from '../../hooks/useGoals';
import { useContentLinks } from '../../hooks/useContentLinks';
import { useToast } from '../../context/ToastContext';
import { CheckIcon } from '@heroicons/react/24/outline';

interface GoalSelectorProps {
  cardId?: string;
  onGoalsUpdated?: (goalIds: string[]) => void;
  onChange?: (goalIds: string[]) => void;
  initialSelectedGoals?: string[];
  className?: string;
  sourceType?: 'career_card' | 'note' | 'review';
}

const GoalSelector: React.FC<GoalSelectorProps> = ({ 
  cardId, 
  onGoalsUpdated,
  onChange,
  initialSelectedGoals = [],
  className = '',
  sourceType = 'career_card'
}) => {
  const { showToast } = useToast();
  const { focus } = useActiveCareerFocus();
  const { goals, loading: goalsLoading } = useGoals(focus?.id || null);
  const { 
    createBulkLinks,
    deleteLinksByCriteria, 
    getLinksForItem,
    isLinking 
  } = useContentLinks();
  
  const [selectedGoals, setSelectedGoals] = useState<Set<string>>(new Set(initialSelectedGoals));
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Load existing goal links
  useEffect(() => {
    const loadExistingLinks = async () => {
      if (!focus || !goals) {
        setIsLoading(false);
        return;
      }

      // If no cardId, use initial selected goals
      if (!cardId) {
        setSelectedGoals(new Set(initialSelectedGoals));
        setIsLoading(false);
        return;
      }

      try {
        const links = await getLinksForItem(sourceType, cardId, 'source');
        const goalLinks = links.filter(link => link.target_type === 'goal');
        const linkedGoalIds = goalLinks.map(link => link.target_id);
        setSelectedGoals(new Set(linkedGoalIds));
        setHasChanges(false); // Reset changes flag after loading
      } catch (error) {
        console.error('Failed to load goal links:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingLinks();
  }, [cardId, focus?.id, goals, sourceType, getLinksForItem]); // Depend on IDs to prevent unnecessary re-renders

  const handleGoalToggle = (goalId: string) => {
    const newSelected = new Set(selectedGoals);
    if (newSelected.has(goalId)) {
      newSelected.delete(goalId);
    } else {
      newSelected.add(goalId);
    }
    setSelectedGoals(newSelected);
    setHasChanges(true); // Mark that we have changes
    
    // If onChange is provided and no cardId, use onChange instead of saving
    if (onChange && !cardId) {
      onChange(Array.from(newSelected));
    }
  };

  const handleSave = async () => {
    if (!focus || isLinking || !cardId) return;

    try {
      // First, remove all existing goal links for this card
      await deleteLinksByCriteria(sourceType, cardId, 'goal');
      
      // Then create new links for each selected goal
      if (selectedGoals.size > 0) {
        const links = Array.from(selectedGoals).map(goalId => ({
          sourceType: sourceType,
          sourceId: cardId,
          targetType: 'goal' as const,
          targetId: goalId,
          metadata: { 
            focus_id: focus.id 
          }
        }));
        
        await createBulkLinks(links);
      }
      
      setHasChanges(false); // Reset changes flag after successful save
      if (onGoalsUpdated) {
        onGoalsUpdated(Array.from(selectedGoals));
      }
    } catch (error) {
      console.error('Failed to save goal links:', error);
      showToast('Failed to update goal links', 'error');
    }
  };

  // Auto-save when goals change (only if we have a cardId)
  useEffect(() => {
    if (!isLoading && !goalsLoading && cardId && hasChanges) {
      // Debounce the save to prevent rapid fire requests
      const timeoutId = setTimeout(() => {
        handleSave();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedGoals, hasChanges, isLoading, goalsLoading, cardId]);

  // Save on unmount if there are pending changes
  useEffect(() => {
    return () => {
      // Save synchronously on unmount if we have unsaved changes
      if (hasChanges && cardId && !isLinking) {
        // Note: This is a fire-and-forget save on unmount
        handleSave();
      }
    };
  }, [hasChanges, cardId, isLinking]);

  if (!focus || !goals || goals.length === 0) {
    return null;
  }

  return (
    <div className={`${className}`}>
      <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
        Link to Personal Goals {focus.company && `(${focus.company} - ${focus.role})`}
      </label>
      
      <div className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 border rounded-lg p-3">
        {isLoading || goalsLoading ? (
          <div className="animate-pulse">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
          </div>
        ) : (
          <div className="space-y-2">
            {goals.map((goal) => (
              <label
                key={goal.id}
                className={`flex items-start gap-3 p-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  isLinking ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedGoals.has(goal.id)}
                  onChange={() => handleGoalToggle(goal.id)}
                  disabled={isLinking}
                  className="mt-0.5 h-4 w-4 rounded text-blue-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
                <span className="flex-1 text-sm text-gray-800 dark:text-gray-200">
                  {goal.goal_text}
                </span>
              </label>
            ))}
          </div>
        )}
        
        {selectedGoals.size > 0 && (
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-3">
            {selectedGoals.size} goal{selectedGoals.size !== 1 ? 's' : ''} selected
          </p>
        )}
      </div>
    </div>
  );
};

export default GoalSelector;